import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import {
  apiService,
  type AuthUser,
  type LoginRequest,
  type ChangePasswordRequest,
} from '@/services/api'
import { logger } from '@/utils/logger'
import { normalizeRole, normalizeRoles } from '@/utils/roles'

// Global authentication state
const isAuthenticated = ref(apiService.isAuthenticated())
const currentUser = ref<AuthUser | null>(null)
const authError = ref('')
const authLoading = ref(false)
const overview = ref<{ users: number } | null>(null)
const authBootstrapReady = ref(!isAuthenticated.value)
const readAccessToken = (): string | null => {
  if (typeof apiService.getAccessToken !== 'function') {
    return null
  }

  try {
    return apiService.getAccessToken()
  } catch (error) {
    logger.error('Failed to read access token:', error)
    return null
  }
}

const refreshAccessToken = async (): Promise<boolean> => {
  if (typeof apiService.refreshAccessToken !== 'function') {
    return false
  }

  try {
    return await apiService.refreshAccessToken()
  } catch (error) {
    logger.error('Failed to refresh access token:', error)
    return false
  }
}

const safeLogout = async (context: string): Promise<void> => {
  try {
    await apiService.logout()
  } catch (error) {
    logger.error(context, error)
  }
}

let hydratedAccessToken: string | null = isAuthenticated.value ? readAccessToken() : null
let authBootstrapAttemptedToken: string | null | undefined = undefined
let authBootstrapPromise: Promise<boolean> | null = null
let authBootstrapInitialized = false
let sessionExpiredHandlerRegistered = false
let authStateVersion = 0
let authStorageSyncRegistered = false

const initializeAuthBootstrapState = () => {
  if (authBootstrapInitialized) {
    return
  }

  hydratedAccessToken = readAccessToken()
  authBootstrapInitialized = true
}

export const hasAuthenticatedSession = computed(() => isAuthenticated.value)
export const mustChangePassword = computed(() => currentUser.value?.must_change_password === true)
const hasEmailVerificationMetadata = computed(
  () =>
    currentUser.value?.email_verified !== undefined ||
    currentUser.value?.email_verified_at !== undefined,
)
const hasPhoneVerificationMetadata = computed(
  () =>
    currentUser.value?.phone_verification_required !== undefined ||
    currentUser.value?.phone_verified !== undefined ||
    currentUser.value?.phone_verified_at !== undefined,
)
export const emailVerificationPending = computed(
  () => hasEmailVerificationMetadata.value && currentUser.value?.email_verified !== true,
)
export const phoneVerificationPending = computed(
  () =>
    hasPhoneVerificationMetadata.value &&
    (currentUser.value?.phone_verification_required === true ||
      currentUser.value?.phone_verified === false),
)
export const hasPendingVerification = computed(
  () => {
    if (isAuthenticated.value && !authBootstrapReady.value && currentUser.value === null) {
      return true
    }

    return emailVerificationPending.value || phoneVerificationPending.value
  },
)
export const verificationState = computed(() => ({
  emailPending: emailVerificationPending.value,
  phonePending: phoneVerificationPending.value,
  requiresVerification: hasPendingVerification.value,
}))

const resetAuthState = () => {
  authStateVersion += 1
  isAuthenticated.value = false
  currentUser.value = null
  authError.value = ''
  authLoading.value = false
  overview.value = null
  hydratedAccessToken = null
  authBootstrapAttemptedToken = undefined
  authBootstrapPromise = null
  authBootstrapInitialized = false
  authBootstrapReady.value = true
}

const markAuthenticatedSession = () => {
  authStateVersion += 1
  initializeAuthBootstrapState()
  isAuthenticated.value = true
  authBootstrapReady.value = false
  hydratedAccessToken = readAccessToken()
}

const hydrateCurrentUser = async () => {
  const sessionVersion = authStateVersion
  const fetchedUser = await apiService.getCurrentUser()
  if (sessionVersion !== authStateVersion) {
    return currentUser.value
  }
  currentUser.value = fetchedUser
  hydratedAccessToken = readAccessToken()
  authBootstrapReady.value = true

  if (currentUser.value?.must_change_password || hasPendingVerification.value) {
    overview.value = null
    return currentUser.value
  }

  await loadOverview()
  return currentUser.value
}

export const ensureAuthReady = async () => {
  initializeAuthBootstrapState()
  const accessToken = readAccessToken()
  const sessionVersion = authStateVersion

  if (currentUser.value) {
    isAuthenticated.value = true
    hydratedAccessToken = accessToken
    authBootstrapReady.value = true
    return true
  }

  if (authBootstrapAttemptedToken === accessToken && hydratedAccessToken === accessToken) {
    return false
  }

  if (authBootstrapPromise) {
    return authBootstrapPromise
  }

  authBootstrapPromise = (async () => {
    try {
      if (!accessToken) {
        const refreshed = await refreshAccessToken()
        if (!refreshed) {
          authBootstrapAttemptedToken = accessToken
          return false
        }
      }

      await hydrateCurrentUser()
      if (sessionVersion !== authStateVersion) {
        return false
      }
      hydratedAccessToken = readAccessToken()
      authBootstrapAttemptedToken = hydratedAccessToken
      isAuthenticated.value = true
      authBootstrapReady.value = true
      return true
    } catch (error) {
      logger.error('Failed to get current user:', error)
      await safeLogout('Falha ao encerrar sessão durante bootstrap; limpando estado local')
      resetAuthState()
      authBootstrapAttemptedToken = readAccessToken()
      return false
    } finally {
      authBootstrapPromise = null
    }
  })()

  return authBootstrapPromise
}

const registerAuthStorageSync = () => {
  if (authStorageSyncRegistered || typeof window === 'undefined') {
    return
  }

  window.addEventListener('storage', (event) => {
    if (
      event.key === 'falcon_refresh_token' ||
      event.key === 'falcon_refresh_token_expiry'
    ) {
      if (event.newValue === null) {
        resetAuthState()
      }
    }
  })

  authStorageSyncRegistered = true
}

const loadOverview = async () => {
  if (!isAuthenticated.value) {
    overview.value = null
    return
  }
  if (!hasRole('administrator')) {
    overview.value = null
    return
  }
  try {
    const userList = await apiService.listUsers()
    overview.value = {
      users: userList.length,
    }
  } catch (error) {
    logger.warn('Falha ao carregar overview de usuários', error)
    overview.value = null
  }
}

const normalizedRoles = computed<string[]>(() => normalizeRoles(currentUser.value?.roles))

export const hasRole = (role: string): boolean => {
  const normalized = normalizeRole(role)
  if (!normalized) return false
  return normalizedRoles.value.includes(normalized)
}

export function useAuth() {
  const router = useRouter()
  registerAuthStorageSync()

  if (!sessionExpiredHandlerRegistered) {
    apiService.setSessionExpiredHandler(async () => {
      await router.push('/')
    })
    sessionExpiredHandlerRegistered = true
  }

  // Computed properties
  const isLoggedIn = computed(() => isAuthenticated.value)
  const user = computed(() => currentUser.value)
  const error = computed(() => authError.value)
  const loading = computed(() => authLoading.value)

  // Login function
  const login = async (credentials: LoginRequest) => {
    authLoading.value = true
    authError.value = ''

    try {
      const response = await apiService.login(credentials)

      // Update global state
      markAuthenticatedSession()

      const profile = await hydrateCurrentUser()

      if (hasPendingVerification.value) {
        await router.push('/verification')
        return response
      }

      if (profile?.must_change_password) {
        await router.push('/change-password')
        return response
      }

      // Redirect to dashboard
      await router.push('/dashboard')

      return response
    } catch (error) {
      logger.error('Login error:', error)

      if (isAuthenticated.value) {
        await safeLogout('Logout error during login recovery:')
        resetAuthState()
      }

      // Handle specific error types
      if (error instanceof Error) {
        if (error.message.includes('conectar ao servidor')) {
          authError.value = 'Não foi possível conectar ao servidor. Verifique sua conexão de rede.'
        } else if (error.message.includes('Invalid credentials') || error.message.includes('401')) {
          authError.value = 'Usuário ou senha incorretos. Tente novamente.'
        } else {
          authError.value = error.message
        }
      } else {
        authError.value = 'Erro inesperado durante o login. Tente novamente.'
      }

      throw error
    } finally {
      authLoading.value = false
    }
  }

  // Logout function
  const logout = async () => {
    authLoading.value = true

    try {
      await apiService.logout()
    } catch (error) {
      logger.error('Logout error:', error)
    } finally {
      // Always clear local state
      resetAuthState()

      // Redirect to login
      await router.push('/')
    }
  }

  // Initialize user data if authenticated
  const initializeAuth = async () => {
    const hadSession = apiService.isAuthenticated()
    const restored = await ensureAuthReady()
    if (!restored && hadSession) {
      await router.push('/')
    }
  }

  const refreshCurrentUser = async () => {
    if (!(await ensureAuthReady())) {
      return null
    }

    try {
      const sessionVersion = authStateVersion
      const current = await hydrateCurrentUser()
      if (sessionVersion !== authStateVersion) {
        return null
      }
      return current
    } catch (error) {
      logger.error('Failed to refresh current user:', error)
      await safeLogout('Logout error during current user refresh:')
      resetAuthState()

      throw error
    }
  }

  // Change password: calls API, then logs out and redirects to login
  const changePassword = async (payload: ChangePasswordRequest) => {
    authLoading.value = true
    authError.value = ''
    try {
      await apiService.changePassword(payload)
      // Require a confirmed backend logout before treating the flow as successful.
      const loggedOut = await apiService.logout({ preserveLocalSessionOnFailure: true })
      if (!loggedOut) {
        authError.value =
          'A senha foi alterada, mas não foi possível encerrar a sessão. Tente sair novamente.'
        throw new Error(authError.value)
      }

      resetAuthState()
      await router.push('/')
    } catch (error) {
      if (!authError.value && error instanceof Error) {
        authError.value = error.message
      } else if (!authError.value) {
        authError.value = 'Erro inesperado ao alterar a senha. Tente novamente.'
      }
      throw error
    } finally {
      authLoading.value = false
    }
  }

  // Clear error
  const clearError = () => {
    authError.value = ''
  }

  const primaryRole = computed(() => normalizedRoles.value[0] ?? '')

  // Check if user is admin
  const isAdmin = computed(() => hasRole('administrator'))

  // Check if user is pilot
  const isPilot = computed(() => hasRole('pilot'))

  return {
    // State
    isLoggedIn,
    user,
    error,
    loading,
    isAdmin,
    isPilot,
    overview,
    mustChangePassword,

    // Actions
    login,
    logout,
    changePassword,
    initializeAuth,
    clearError,
    hasRole,
    roles: normalizedRoles,
    primaryRole,
    loadOverview,
    refreshCurrentUser,
    hasPendingVerification,
    verificationState,
  }
}
